import { Injectable } from '@angular/core';
import { FormElement, FormElementType } from '../../../../../libs/form-designer/projects/pdf-form-designer/src/public-api';
import * as Communication from '../../communication/index';

/**
 * Service for managing form element operations including conversion between
 * FormElement (form-designer format) and DocumentField (REST API format).
 *
 * This service provides:
 * - Conversion between FormElement and DocumentField formats
 * - Type mapping between form element types and field types
 * - Options/properties transformation
 * - ID conversion helpers
 * - (Future) Validation, manipulation, and utility methods
 */
@Injectable({
  providedIn: 'root'
})
export class FormElementService {

  constructor() { }

  // =====================================
  // Conversion Methods
  // =====================================

  /**
   * Convert FormElement array to DocumentField array for API submission
   * @param elements FormElement array from pdf-form-designer
   * @param documentId ID of the document these fields belong to
   * @param signerIdToRecipientIdMap Map of signer ID to recipient ID
   * @returns Array of DocumentField objects for REST API
   */
  convertFormElementsToFields(
    elements: FormElement[],
    documentId: number,
    signerIdToRecipientIdMap: Map<string, number>
  ): any[] {
    return elements.map((element, index) => {
      const field: any = {
        documentId: documentId,
        recipientId: this.getRecipientIdFromSignerId(element.signerId, signerIdToRecipientIdMap),
        page: (element.pageIndex || 0) + 1, // Convert to 1-based index
        type: this.mapFormElementTypeToField(element.type),
        width: element.pdfPosition?.width || element.position?.width || 100,
        height: element.pdfPosition?.height || element.position?.height || 30,
        x: element.pdfPosition?.x || element.position?.x || 0,
        y: element.pdfPosition?.y || element.position?.y || 0,
        label:  (element.properties as any)?.placeholder || element.name || '',
        enabled: !(element.properties as any)?.readOnly,
        initialValue: (element.properties as any)?.defaultValue || '',
        value: '',
        variable: element.name ,
        validation: '',
        orderno: index,
        required: (element.properties as any)?.required || false,
        options: JSON.stringify(this.extractFieldOptions(element))
      };

      return field;
    });
  }

  /**
   * Convert DocumentField array from API to FormElement array for pdf-form-designer
   * @param fields DocumentField array from REST API
   * @param recipientIdToSignerIdMap Map of recipient ID to signer ID
   * @returns Array of FormElement objects for form-designer
   */
  convertFieldsToFormElements(
    fields: Communication.DocumentField[],
    recipientIdToSignerIdMap: Map<number, string>
  ): FormElement[] {
    return fields.map((field, index) => {
      if (field.options == "null"){
        field.options = "";
      }
      const options = field.options ? JSON.parse(field.options) : {};

      const element: FormElement = {
        id: `field-${field.id || index}`,
        type: this.mapFieldTypeToFormElement(field.type),
        name: field.variable,
        pageIndex: Number(field.page || 1) - 1, // Convert to 0-based index
        position: {
          x: Number(field.x || 0),
          y: Number(field.y || 0),
          width: Number(field.width || 100),
          height: Number(field.height || 30),
          // Include angle if it exists in options
          ...(options.rotateAngle !== undefined && { angle: Number(options.rotateAngle) })
        },
        pdfPosition: {
          x: Number(field.x || 0),
          y: Number(field.y || 0),
          width: Number(field.width || 100),
          height: Number(field.height || 30),
          // Include angle if it exists in options
          ...(options.rotateAngle !== undefined && { angle: Number(options.rotateAngle) })
        },
        pageDimensions: {
          width: 612,  // US Letter width in PDF points
          height: 792  // US Letter height in PDF points
        },
        properties: {
          required: field.required || false,
          readOnly: !field.enabled,
          defaultValue: field.initialValue || '',
          ...this.mapOptionsToProperties(options),

        },
        signerId: this.getSignerIdFromRecipientId(Number(field.recipientId), recipientIdToSignerIdMap)
      };

      return element;
    });
  }

  // =====================================
  // Type Mapping Methods
  // =====================================

  /**
   * Map FormElementType enum to field type string used in REST API
   * @param type FormElementType enum value
   * @returns Field type string for REST API
   */
  mapFormElementTypeToField(type: string): string {
    const typeMap: { [key: string]: string } = {
      'text-input': 'text-input',
      'checkbox': 'checkbox',
      'radio-button': 'radio',
      'signature': 'sign-area',
      'text': 'text-area',
      'biometric-signature': 'bio-sign',
      'initials': 'initials',
      'file-list': 'file-list'
    };

    return typeMap[type] || 'text-input';
  }

  /**
   * Map field type string from REST API to FormElementType enum
   * @param fieldType Field type string from REST API
   * @returns FormElementType enum value
   */
  mapFieldTypeToFormElement(fieldType: string): FormElementType {
    const typeMap: { [key: string]: FormElementType } = {
      'text-input': FormElementType.TEXT_INPUT,
      'checkbox': FormElementType.CHECKBOX,
      'radio': FormElementType.RADIO_BUTTON,
      'sign-area': FormElementType.SIGNATURE,
      'text-area': FormElementType.TEXT,
      'bio-sign': FormElementType.BIOMETRIC_SIGNATURE,
      'initials': FormElementType.INITIALS,
      'file-list': FormElementType.ATTACHMENTS_LIST
    };

    return typeMap[fieldType] || FormElementType.TEXT_INPUT;
  }

  // =====================================
  // Options/Properties Mapping Methods
  // =====================================

  /**
   * Extract field options from FormElement properties for REST API
   * Maps FormElement property names to REST API field names
   * @param element FormElement with properties
   * @returns Options object for REST API
   */
  extractFieldOptions(element: FormElement): any {
    const options: any = {};

    if (element.properties) {
      const props = element.properties as any;

      // Common properties
      if (props.fontSize) options.fontSize = props.fontSize;
      if (props.fontFamily) options.fontFamily = props.fontFamily;
      if (props.backgroundColor) options.backgroundColor = props.backgroundColor;
      if (props.borderColor) options.borderColor = props.borderColor;
      if (props.borderWidth) options.borderWidth = props.borderWidth;
      if (props.placeholder) options.placeholder = props.placeholder;

      // Text-specific properties (use REST API field names)
      if (props.textColor) options.color = props.textColor; // Map textColor -> color
      if (props.angle !== undefined) options.rotateAngle = props.angle; // Map angle -> rotateAngle
      if (props.text) options.text = props.text;
      if (props.textAlign) options.textAlign = props.textAlign;
      if (props.fontWeight) options.fontWeight = props.fontWeight;
      if (props.italic !== undefined) options.italic = props.italic;
      if (props.underline !== undefined) options.underline = props.underline;
      if (props.transparency !== undefined) options.transparency = props.transparency;

      //Radio Specific properties
      if (props.selectedValue) {
        options.selectedValue =  props.selectedValue;
      }

      // Direct mapping for legacy REST API field names
      if (props.color) options.color = props.color;
    }

    return options;
  }

  /**
   * Map REST API options field names to FormElement property names
   * @param options Options object from REST API
   * @returns Properties object for FormElement
   */
  mapOptionsToProperties(options: any): any {
    const properties: any = { ...options };

    // Map REST API field names to FormElement property names
    if (options.color !== undefined) {
      properties.textColor = options.color; // Map color -> textColor
      delete properties.color; // Remove the REST API field name
    }

    if (options.rotateAngle !== undefined) {
      properties.angle = Number(options.rotateAngle); // Map rotateAngle -> angle
      delete properties.rotateAngle; // Remove the REST API field name
    }

    return properties;
  }

  // =====================================
  // ID Conversion Helper Methods
  // =====================================

  /**
   * Get recipient ID from signer ID using a map
   * @param signerId Signer ID from FormElement
   * @param signerIdToRecipientIdMap Map of signer ID to recipient ID
   * @returns Recipient ID (number) or 0 if not found
   */
  getRecipientIdFromSignerId(
    signerId: string,
    signerIdToRecipientIdMap: Map<string, number>
  ): number {
    return signerIdToRecipientIdMap.get(signerId) || 0;
  }

  /**
   * Get signer ID from recipient ID using a map
   * @param recipientId Recipient ID from DocumentField
   * @param recipientIdToSignerIdMap Map of recipient ID to signer ID
   * @returns Signer ID (string) or recipient ID as string if not found
   */
  getSignerIdFromRecipientId(
    recipientId: number,
    recipientIdToSignerIdMap: Map<number, string>
  ): string {
    return recipientIdToSignerIdMap.get(recipientId) || recipientId.toString();
  }

  // =====================================
  // PDF Form Data Methods
  // =====================================

  /**
   * Sync PDF form data values to application fields
   * Maps PDF field names to application field IDs and updates both internal state and field values
   * @param fields Array of DocumentField objects
   * @param pdfFormData Object containing PDF form field values keyed by field name
   * @returns Object with formFieldValues map (fieldId -> value)
   */
  syncPdfFormDataToFields(
    fields: any[],
    pdfFormData: { [fieldName: string]: any }
  ): { [fieldId: string]: any } {
    const formFieldValues: { [fieldId: string]: any } = {};

    // Map PDF field names to our application's field IDs
    fields.forEach((field: any) => {
      if (field.type === 'text-input' || field.type === 'checkbox' || field.type === 'radio') {
        const fieldName = field.variable || field.label || `field_${field.id}`;

        if (pdfFormData[fieldName] !== undefined) {
          // Update formFieldValues for internal state
          if (field.type === 'radio') {
            if (String(pdfFormData[fieldName]).toLowerCase() === 'on') {
              formFieldValues[field.id] = true;
            }
            else{
              formFieldValues[field.id] = false;
            }
          }
          else{
            formFieldValues[field.id] = pdfFormData[fieldName];
          }

          // Update field.value for API submission
          field.value = String(pdfFormData[fieldName]);

          console.log(`Captured field "${fieldName}" (ID: ${field.id}):`, pdfFormData[fieldName]);
        }
      }
    });

    return formFieldValues;
  }

  // =====================================
  // Future: Validation Methods (placeholder)
  // =====================================
  // TODO: Add validation methods as needed
  // validateFormElement(element: FormElement): boolean { }
  // validateFormConfiguration(config: FormConfiguration): boolean { }
  // validateRequiredFields(elements: FormElement[]): boolean { }

  // =====================================
  // Future: Manipulation Methods (placeholder)
  // =====================================
  // TODO: Add manipulation methods as needed
  // cloneFormElement(element: FormElement): FormElement { }
  // moveElement(element: FormElement, newPosition: Position): FormElement { }
  // resizeElement(element: FormElement, newDimensions: Dimensions): FormElement { }

  // =====================================
  // Future: Utility Methods (placeholder)
  // =====================================
  // TODO: Add utility methods as needed
  // getElementsByType(elements: FormElement[], type: FormElementType): FormElement[] { }
  // filterElementsByRecipient(elements: FormElement[], signerId: string): FormElement[] { }
  // calculateBounds(elements: FormElement[]): Bounds { }
}
